import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PreviewRoleContext from '../../context/PreviewRoleContext';
import SendPointer from '../../components/SendPointer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { goBack } from '../../utils/trail';
import AdminLayout from './AdminLayout';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import { jobName } from '../../utils/ui';
import JobBanner, { computeAddressee } from './JobBanner';
import JobLocationModal from '../../components/JobLocationModal';
import PdfWindow from '../../components/PdfWindow';
import { CreatePoModal } from './CreatePoModal';
import { DrillCardModal, NveCardModal, UsaCardModal, LoopsCardModal, workflowCardFromSubJob } from './AdminWorkflows';
import NoteCardModal from './NoteCardModal';
import { usePillowItems, countLabel, ItemRow, PoRow, addPillowItem, sortItems, hasItemSource, PillowItemList } from './PillowItems';
import {
  ShopTab, PayrollTab, SubmittalsTab, UsaDigsTab, NVETab,
  LoopsTab, ExtraWorkTab, TMBillingSection, EquipmentJobCard, MobileBidShell, STATUS_META,
  VendorQuotesSection, LocationPickerModal,
} from './AdminBidDetail';
import JobHeaderPillow from './JobInfoPillow';
import RfiPillow from './RfiPillow';
import TmTile from './TmTile';
import ExtraWorkDaily, { TmProjectTracker } from './ExtraWorkCard';
import DocumentsPillow from './DocumentsPillow';
import WoBillingPillow from './WoBillingPillow';
import { ShopOrdersBoard } from './AdminShopOrders';
import { FieldwirePillow, FieldwireTasks, FieldwireSheetsPillow } from './FieldwirePanel';
import {
  NotesPillow, DailyUpdatesPillow, PunchListPillow,
  CloseOutPillow, WorkOrdersPillow, PurchaseOrdersPillow, RecipientsPillow,
  LineItemsPillow, NotesOnlyPillow, NotYetMoved, SchedulePillow, BigPicturePillow,
  TsheetsPillow,
} from './JobPillows';
import { JobPhotosPillow } from './JobPhotos';
import { JobPermitsPillow } from './PermitsBoard';

// ─────────────────────────────────────────────────────────────────────────────
// The job page, rebuilt as five tiles.
//
// The old page (AdminBidDetail) splits everything into a "Zone 1 — Admin" half and
// a "Zone 2 — Field" half. Here the page is five tiles and the zone travels with the
// PILLOW instead: a field supervisor opens the same five tiles and finds fewer
// pillows inside them. That's why mobile needs no separate design.
//
// Placement lives in the pillow_layout table — keyed by pillow, not by job, so the
// layout is identical on every job. While it's being settled an Executive gets two
// pickers on each pillow; once it's right the pickers come out and the arrangement
// stays.
//
// Nothing here reimplements a pillow. Every one that has a real component imports
// the SAME component the old page uses, so behaviour can't drift between the two.
// ─────────────────────────────────────────────────────────────────────────────

const TILES = [
  { key: 'job_info',   label: 'Job Information',       blurb: 'What the job is, what it’s worth, and what’s happened on it.' },
  { key: 'work_flows', label: 'Work Flows',            blurb: 'The trades and processes this job runs through.' },
  { key: 'material',   label: 'Material and Equipment', blurb: 'What was bought and what iron is on the job.' },
  { key: 'plans',      label: 'Plans and Documents',   blurb: 'Drawings and documents, and the mark-ups on them.' },
  { key: 'changes',    label: 'Changes',               blurb: 'Work beyond the contract.' },
  { key: 'schedule',   label: 'Schedule and Daily Updates', blurb: 'Who’s booked on this job, and what the crew reported.' },
];

const ZONE_LABEL = { '1': 'Zone 1 · Office', '2': 'Zone 2 · Field', both: 'Both' };
const ZONE_STYLE = {
  '1':  { bg: '#e9eef5', fg: '#1e3a5f' },
  '2':  { bg: '#e5f2ec', fg: '#0f6b4a' },
  both: { bg: '#f1eee9', fg: '#57534e' },
};

// Every pillow on the page, with the sub-job type it maps to where it is one.
// `sub` means the pillow only appears when the job actually has that workflow card.
const PILLOWS = [
  { key: 'job_header',          label: 'Job header' },
  // Headline numbers and Cost summary folded into Bid & Estimate (Sep 2026).
  { key: 'wo_billing',          label: 'Billing' },
  { key: 'tsheets',             label: 'QuickBooks Time' },
  { key: 'fieldwire',           label: 'Fieldwire Tasks' },
  { key: 'fieldwire_sheets',    label: 'Fieldwire Sheets' },   // the plan sheets as thumbnails, opened with every pin on them
  // Bid & Estimate came off the page (Pat, Sep 23 2026): the bid page is one click from the
  // Job header, and the Line Items table carries the prices. Old placements for it are ignored.
  { key: 'bid_recipients',      label: 'Bid Recipients' },
  { key: 'bid_notes',           label: 'Notes' },
  { key: 'job_photos',          label: 'Photos' },   // every picture on the job — phone uploads, cards, holes, loops (Ryan, Sep 29 2026)
  { key: 'contract',            label: 'Contract',             sub: 'contract' },
  { key: 'close_out',           label: 'Close Out Checklist' },
  { key: 'line_items',          label: 'Line Items' },
  { key: 'daily_updates',       label: 'Daily Updates' },
  // These open the SAME card the Vertical Drilling board opens — not a copy of it.
  { key: 'vertical_drilling',   label: 'Vertical Drilling',    sub: 'vertical_drilling', opensCard: 'drill', size: 'large' },
  { key: 'horizontal_drilling', label: 'Directional Drilling', sub: 'horizontal_drilling', opensCard: 'drill', size: 'large' },
  { key: 'loops',               label: 'Loops',                sub: 'loops', opensCard: 'loops', size: 'large' },
  { key: 'nve',                 label: 'NVE',                  sub: 'nve', opensCard: 'nve', size: 'large' },
  { key: 'usa_digs',            label: 'USA Digs',             sub: 'usa_digs', opensCard: 'usa', size: 'large' },
  { key: 'shop',                label: 'Shop',                 sub: 'shop' },
  { key: 'submittals',          label: 'Submittals',           sub: 'submittals' },
  { key: 'subcontracts',        label: 'Subcontracts',         sub: 'subcontracts' },
  { key: 'payroll',             label: 'Payroll',              sub: 'payroll' },
  { key: 'misc',                label: 'Miscellaneous',        sub: 'misc', opensCard: 'note', size: 'large' },
  { key: 'custom',              label: 'Custom',               sub: 'custom', opensCard: 'note', size: 'large' },
  { key: 'punch_list',          label: 'Punch List' },
  { key: 'work_orders',         label: 'Work Orders' },
  { key: 'purchase_orders',     label: 'Purchase Orders' },
  { key: 'permits',             label: 'Permits' },   // the job's permits — closed ones land here off the Permits board (Pat, Oct 9 2026)
  { key: 'shop_order',          label: 'Shop Orders',          sub: 'shop_order' },
  { key: 'equipment_on_job',    label: 'Equipment on this job' },
  { key: 'vendor_quotes',       label: 'Vendor Quotes' },
  // Bid Documents & Plans came off the job page (Pat, Sep 23 2026): the bid's files are
  // already in the Documents ledger (Drawings & Plans / Vendor Quotes), so one list.
  { key: 'documents',           label: 'Documents' },
  { key: 'rfi',                 label: 'RFIs' },
  { key: 'extra_work',          label: 'Extra Work',           sub: 'extra_work' },
  { key: 'change_orders',       label: 'Change Orders',        sub: 'change_orders' },
  { key: 'tm_pricing',          label: 'T&M Rates' },
  { key: 'tm_billing',          label: 'Project T&M Items' },   // was "Standard T&M" (and "T&M Billing" before that) — Pat, Sep 23 2026; the job's price structure leads on each day inside it
  { key: 'big_picture',         label: 'Big Picture Schedule' },
  { key: 'job_schedule',        label: 'Crew Schedule' },
];

// The nine the Work Flows board offers. Punch List is one of them and is pinned last;
// Miscellaneous is a fixed catch-all and Custom asks for a name.
const WORKFLOW_CHOICES = [
  { key: 'vertical_drilling',   label: 'Vertical Drilling',   icon: '⬇️' },
  { key: 'horizontal_drilling', label: 'Directional Drilling', icon: '➡️' },
  { key: 'loops',               label: 'Loops',               icon: '🔁' },
  { key: 'nve',                 label: 'NVE',                 icon: '⚡' },
  { key: 'usa_digs',            label: 'USA Digs',            icon: '🗺️' },
  // A work order made without one had no way to get it (WO26141, Oct 6 2026).
  { key: 'submittals',          label: 'Submittals',          icon: '📑' },
  { key: 'extra_work',          label: 'Extra Work',          icon: '🛠️' },
  { key: 'work_orders',         label: 'Work Orders',         icon: '🧾' },
  { key: 'misc',                label: 'Miscellaneous',       icon: '📦' },
  { key: 'custom',              label: 'Custom',              icon: '✏️', named: true },
  { key: 'punch_list',          label: 'Punch List',          icon: '✅' },
];
// Shown on every job even before the job has the card — with "+ Add to this job".
const ALWAYS_OFFERED = ['extra_work', 'change_orders'];
// Punch List sits at the bottom of Work Flows, always, with the add tile after it.
const PINNED_LAST = 'punch_list';
// These two have no workflow card behind them — Punch List reads the job's punch
// items and Work Orders the work orders under it. So adding and removing them is
// hiding and unhiding the TAB on that job, not creating or deleting a card. The data
// itself is never touched: hide the tab and the punch items are still there if it's
// added back.
const CARDLESS = ['punch_list', 'work_orders'];

// Opened out, most pillows show their own component — the Extra Work tab, the USA
// dig tickets, the NVE meters — because those are documents with detail behind them
// and the row list is only a summary of what they hold.
//
// Purchase Orders is the exception: its panel showed LESS than the tile did, so for
// that one the list IS the full view, with the room used for a search box. Don't
// widen this list without checking the component is genuinely poorer than the rows.
const LIST_IS_THE_FULL_VIEW = ['purchase_orders'];
// Pillows that open over the WHOLE screen instead of in a row under their tile: a board
// that needs the width (Submittals — seven columns left to right; Ryan, Oct 2 2026).
const FULL_SCREEN_PILLOWS = ['submittals'];

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' };

// How tall the pillows stand depends on how many there are, because the page has the
// same height either way. Three fit on one row, so they run as long as their contents
// need — nothing is hidden behind a scrollbar. Six make two rows and get a taller
// window than the old fixed one, so more shows without the second row falling off the
// page. Past that it goes back to a compact window. A row is always equal height:
// null means "as long as the tallest of you", not "every pillow for itself".
function pillowHeight(count, embedded) {
  if (embedded) return 340;                 // one per row on a phone — keep them uniform
  const rows = Math.ceil(count / 3);
  if (rows <= 1) return null;               // as long as needed
  if (rows === 2) return 460;
  return 360;
}
const selS = { fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' };

// Sits on the OLD job page now that jobs open on the new one, offering the way back.
// No role check: the point of keeping a fallback is that whoever ends up needing it
// can leave again without having to ask anyone.
export function NewLayoutBar({ id }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      background: '#f5f5f4', border: '1px solid #e7e5e4', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
      <span style={{ fontSize: 13, color: '#57534e', flex: 1, minWidth: 220 }}>
        <b>This is the old job page.</b> Jobs open on the new layout now — this one stays here
        for anything the new page can’t do yet.
      </span>
      <button onClick={() => navigate(`/admin/new-projects/${id}`)}
        style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: '#e87722', color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Back to the new layout
      </button>
    </div>
  );
}

// `embedded` renders the field supervisor's view: same five tiles, mobile chrome,
// and only the pillows their zone allows. Same component, so the structure genuinely
// cannot drift between desktop and mobile.
export default function AdminJobDetail({ embedded = false }) {
  // No tile in the URL = the hub. A tile = you're inside it. Keeping it in the URL
  // means Back works and a tile can be linked to directly.
  const { id, tile: tileKey } = useParams();
  // The open pillow lives in the address bar, so a link can name one and so that
  // sending someone "this page" sends what is actually on screen.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  // Another job on this same number? (Ryan, Oct 23 2026: WO26140 is two jobs — plans and
  // Fieldwire went to the wrong one.) Shown red under the header so it gets renumbered.
  const [sameNumber, setSameNumber] = useState(null);
  useEffect(() => {
    const num = project && project.project_number; if (!num || !project.id) { setSameNumber(null); return; }
    apiFetch(`/projects/number-check?number=${encodeURIComponent(num)}&except=${project.id}`).then(r => setSameNumber(r && r.taken ? r.existing : null)).catch(() => setSameNumber(null));
  }, [project && project.id, project && project.project_number]); // eslint-disable-line react-hooks/exhaustive-deps
  const [bid, setBid] = useState(null);
  const [items, setItems] = useState([]);
  const [subJobs, setSubJobs] = useState([]);
  // Fieldwire pins per workflow card ({ [sub_job_id]: { tasks, done } }) — one fetch for the
  // whole page so each pillow can show "83 pins" on its face without asking on its own.
  const [fwPins, setFwPins] = useState({});
  const [layout, setLayout] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [counts, setCounts] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [showLocation, setShowLocation] = useState(false);
  const [pickPin, setPickPin] = useState(false);       // the job's map pin, set from its Location window (Oct 7 2026)
  const [pinKey, setPinKey] = useState(0);
  const [showPo, setShowPo] = useState(false);
  const [usedQuote, setUsedQuote] = useState(null);   // { id, title } — the Used Quote in a window over the page (Ryan, Oct 26 2026)
  const [vendors, setVendors] = useState([]);
  const [drillCardSj, setDrillCardSj] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);   // pillow shown centred
  const [addOpen, setAddOpen] = useState(false);          // the add-a-workflow picker
  const [nveAsk, setNveAsk] = useState(false);             // NVE picked: new service, underground only, or a pole hold? (Ryan, Oct 14 2026)
  const [addBackOpen, setAddBackOpen] = useState(false);  // pillows taken off this page
  const [hidden, setHidden] = useState([]);               // tabs this job doesn't need
  const [openCard, setOpenCard] = useState(null);        // { kind, sj, item } shown centred
  const [employees, setEmployees] = useState([]);

  const showToast = useCallback(msg => { setToast(String(msg || '')); setTimeout(() => setToast(''), 2600); }, []);

  // Role comes from titan_session, the same place every other page reads it — the
  // old job page, the route guards, all of them. This page originally read te_worker
  // instead, which is set at login but isn't the source of truth for role: when it
  // didn't line up the page treated an Executive as field crew and hid every Zone 1
  // pillow, so Contract, the cost summary and the line items silently vanished.
  // …unless the page is being previewed as someone else (Mobile view / View as): then it's their role.
  const previewRole = useContext(PreviewRoleContext);
  const role = previewRole || (() => {
    try {
      const s = JSON.parse(localStorage.getItem('titan_session') || 'null');
      if (s?.role) return s.role;
      return JSON.parse(localStorage.getItem('te_worker') || 'null')?.role || '';
    } catch { return ''; }
  })();
  const isAdminZone = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'].includes(role);
  // Only an Executive rearranges. Everyone else just sees the arrangement.
  const canArrange = ['Executive', 'Admin', 'admin'].includes(role);
  // Editing the job's own facts — owner, GC, pricing — is an Executive's call.
  const isExec = ['Executive', 'Admin', 'admin'].includes(role);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const [proj, lay, hid] = await Promise.all([
          apiFetch(`/projects/${id}`),
          apiFetch('/pillow-layout').catch(() => []),
          apiFetch(`/projects/${id}/hidden-pillows`).catch(() => []),
        ]);
        if (!live) return;
        setProject(proj);
        setSubJobs(Array.isArray(proj?.subJobs) ? proj.subJobs : []);
        setLayout(Array.isArray(lay) ? lay : []);
        setHidden(Array.isArray(hid) ? hid : []);
        // /projects/:id/bid is readable by everyone and gives us the bid's id, which
        // the Plans pillow needs. The FULL bid — and its line items — comes from
        // GET /bids/:id, which is office-only; the field is never shown either, so a
        // 403 there is expected rather than an error.
        const b = await apiFetch(`/projects/${id}/bid`).catch(() => null);
        if (!live) return;
        setBid(b);
        apiFetch(`/bids/${b?.id}/recipients`).then(r => { if (live) setRecipients(Array.isArray(r) ? r : []); }).catch(() => {});
        apiFetch('/vendors/active').then(v => { if (live) setVendors(Array.isArray(v) ? v : []); }).catch(() => {});
        if (b?.id) {
          const full = await apiFetch(`/bids/${b.id}`).catch(() => null);
          if (!live || !full) return;
          setBid(prev => ({ ...prev, ...full }));
          if (Array.isArray(full.items)) setItems(full.items);
        }
      } catch (e) {
        if (live) setError(e?.error || e?.message || 'Could not load this job.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [id]);

  // Small counts for the tile faces, so the hub answers "where does this job stand"
  // rather than just listing five names. Loaded once, alongside the page.
  useEffect(() => {
    let live = true;
    const set = (k, v) => { if (live) setCounts(c => ({ ...c, [k]: v })); };
    apiFetch('/users').then(r => setEmployees(Array.isArray(r) ? r : [])).catch(() => {});   // same list the old page uses for PM names
    apiFetch(`/projects/${id}/purchase-orders`).then(r => set('pos', Array.isArray(r) ? r.length : 0)).catch(() => {});
    apiFetch(`/projects/${id}/punch-list`).then(r => set('punch', Array.isArray(r) ? r.filter(x => x.status !== 'complete').length : 0)).catch(() => {});
    // Distinct people booked on this job over the next week.
    apiFetch(`/projects/${id}/schedule?days=7`)
      .then(r => set('crew', new Set((r?.events || []).flatMap(e => e.crew.map(c => c.id))).size))
      .catch(() => {});
    return () => { live = false; };
  }, [id]);

  useEffect(() => {
    if (!bid?.id) return undefined;
    let live = true;
    apiFetch(`/bids/${bid.id}/attachments`)
      .then(r => { if (live) setCounts(c => ({ ...c, plans: Array.isArray(r) ? r.length : 0 })); })
      .catch(() => {});
    return () => { live = false; };
  }, [bid?.id]);

  const placement = useMemo(() => Object.fromEntries(layout.map(r => [r.pillow_key, r])), [layout]);
  const subJobByType = useMemo(() => {
    const m = {};
    for (const sj of subJobs) if (!m[sj.type]) m[sj.type] = sj;
    return m;
  }, [subJobs]);

  async function move(key, patch) {
    const before = layout;
    setLayout(l => l.map(r => (r.pillow_key === key ? { ...r, ...patch } : r)));   // optimistic
    try {
      await apiFetch(`/pillow-layout/${key}`, { method: 'PUT', body: JSON.stringify(patch) });
    } catch (e) {
      setLayout(before);
      showToast(e?.error || 'Could not move that pillow.');
    }
  }

  // A pillow shows if its zone allows this viewer, and — for workflow pillows — if
  // this job actually has that card. Zone hides the pillow; it never hides prices,
  // which stay behind isAdminZone inside each component.
  //
  // While arranging, a workflow the job does NOT have is still listed, greyed out.
  // Otherwise you can only arrange what the job in front of you happens to carry:
  // Ryan set Vertical Drilling on 2631 and never saw Directional Drilling or Loops,
  // because that job has neither, so they silently kept the old setting.
  function visible(p) {
    const place = placement[p.key];
    if (!place) return false;
    if (hidden.includes(p.key)) return false;   // taken off this job
    if (!canArrange) {
      if (place.zone === '1' && !isAdminZone) return false;
      if (place.zone === '2' && isAdminZone) return false;
      // Extra Work and Change Orders belong on every job: when this one doesn't have them yet they
      // still show (greyed, "+ Add to this job") for the people who can add them — Jenny's 2634 Victory
      // showed only RFIs (Ryan, Oct 12 2026; awards stopped adding these on Oct 6).
      if (p.sub && !subJobByType[p.sub] && !(ALWAYS_OFFERED.includes(p.key) && isAdminZone)) return false;
    }
    return true;
  }

  const byTile = useMemo(() => {
    const out = Object.fromEntries(TILES.map(t => [t.key, []]));
    for (const p of PILLOWS) {
      const place = placement[p.key];
      if (!place || !out[place.tile]) continue;
      if (!visible(p)) continue;
      // Listed for arranging, but this job doesn't carry it — no content to open.
      const missing = !!(p.sub && !subJobByType[p.sub]);
      out[place.tile].push({ ...p, ...place, missing });
    }
    // Work Flows carries only what the job actually needs — a workflow it doesn't have
    // isn't shown at all, it's added from the tile at the end. Everywhere else, an
    // absent pillow still sinks to the bottom rather than vanishing.
    out.work_flows = out.work_flows.filter(x => !x.missing);
    for (const k of Object.keys(out)) {
      out[k].sort((x, y) =>
        (x.key === PINNED_LAST ? 1 : 0) - (y.key === PINNED_LAST ? 1 : 0) ||
        (x.missing ? 1 : 0) - (y.missing ? 1 : 0) ||
        x.sort_order - y.sort_order);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, subJobByType, isAdminZone, canArrange, hidden]);


  // Same status pill and correspondence line the old page shows.
  const statusMeta = bid ? (STATUS_META[bid.status] || STATUS_META.draft) : null;
  const addressee = useMemo(() => computeAddressee(bid, recipients, project), [bid, recipients, project]);

  async function setScheduleWorkflow(v) {
    setProject(p => ({ ...p, schedule_workflow: v }));   // optimistic
    // /work-orders/:id, not /projects/:id — the projects PATCH doesn't know this
    // field and was dropping it, so the picker moved and nothing was saved.
    try { await apiFetch(`/work-orders/${id}`, { method: 'PATCH', body: JSON.stringify({ schedule_workflow: v }) }); }
    catch { showToast('Could not change the schedule board.'); }
  }

  const tileUrl = k => (embedded ? `/sup/jobs/${id}/${k}` : `/admin/jobs/${id}/${k}`);
  const hubUrl = embedded ? `/sup/jobs/${id}` : `/admin/jobs/${id}`;
  const activeTile = TILES.find(t => t.key === tileKey) || null;

  // Following a link that names a pillow. Which tile holds it is a matter of how this
  // job happens to be arranged today, and that changes — so the link carries only the
  // pillow and the page works out where it lives, landing on the right tile either way.
  const wantPillow = searchParams.get('pillow');
  const wantItem = searchParams.get('item');   // one RFI, change order or submittal
  useEffect(() => {
    if (!wantPillow || !layout.length) return;
    const place = placement[wantPillow];
    if (!place) return;
    if (tileKey !== place.tile) {
      navigate(`${hubUrl}/${place.tile}?pillow=${wantPillow}${wantItem ? `&item=${wantItem}` : ''}`, { replace: true });
      return;
    }
    setExpandedKey(k => (k === wantPillow ? k : wantPillow)); scrollToOpen(wantPillow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantPillow, tileKey, layout.length]);

  function openPillowItem(key, itemId) {
    setExpandedKey(key); scrollToOpen(key);
    const sp = new URLSearchParams(searchParams);
    sp.set('pillow', key);
    if (itemId) sp.set('item', itemId); else sp.delete('item');
    setSearchParams(sp, { replace: true });
  }

  const scrollToOpen = key => { if (key) setTimeout(() => { const el = document.getElementById('pillow-open-' + key); if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 80); };
  function openPillowKey(key) {
    setExpandedKey(key); scrollToOpen(key);
    const sp = new URLSearchParams(searchParams);
    if (key) sp.set('pillow', key); else { sp.delete('pillow'); sp.delete('item'); }
    // Replace, not push — closing a pillow should not need two taps of Back.
    setSearchParams(sp, { replace: true });
  }
  const tileRows = tileKey ? (byTile[tileKey] || []) : [];
  const openPillow = tileRows.find(p => p.key === openKey) || null;

  // Add one of the nine to this job. Custom asks for a name; the rest use their own.
  // Drilling's + adds another drilling CARD to the job — a second area with its own
  // rig, date and holes — rather than an item inside the existing one.
  async function addWorkflowCard(type, asks) {
    const name = (window.prompt(asks) || '').trim();
    if (!name) return;
    try {
      await apiFetch(`/projects/${id}/sub-jobs`, { method: 'POST', body: JSON.stringify({ type, name }) });
      await reloadSubJobs();
      showToast(`${name} added.`);
    } catch (e) { showToast(e?.error || 'Could not add that card.'); }
  }

  // Anything taken off this job's page, ready to come back. Placement decides which
  // tile it belongs to, so each tile only ever offers its own.
  function hiddenFor(tile) {
    return PILLOWS.filter(p => hidden.includes(p.key) && placement[p.key]?.tile === tile);
  }

  async function unhidePillow(p) {
    try {
      await apiFetch(`/projects/${id}/hidden-pillows/${p.key}`, { method: 'DELETE' });
      setHidden(h => h.filter(k => k !== p.key));
      setAddOpen(false);
      showToast(`${p.label} added back.`);
    } catch (e) { showToast(e?.error || 'Could not add that back.'); }
  }

  async function addWorkflow(choice, extra = {}) {
    if (choice.key === 'nve' && !('nve_pole_hold' in extra)) { setNveAsk(choice); return; }
    // Punch List and Work Orders have no card to create — adding them puts the tab
    // back, and the punch items and work orders that were there all along reappear.
    if (CARDLESS.includes(choice.key)) {
      try {
        await apiFetch(`/projects/${id}/hidden-pillows/${choice.key}`, { method: 'DELETE' });
        setHidden(h => h.filter(k => k !== choice.key));
        setAddOpen(false);
        showToast(`${choice.label} added.`);
      } catch (e) { showToast(e?.error || 'Could not add that back.'); }
      return;
    }
    const name = choice.named
      ? (window.prompt('What should this workflow be called?') || '').trim()
      : choice.label;
    if (choice.named && !name) return;
    try {
      await apiFetch(`/projects/${id}/sub-jobs`, {
        method: 'POST',
        body: JSON.stringify({ type: choice.key, name: extra.nve_name || name, ...extra }),
      });
      await reloadSubJobs();
      setAddOpen(false); setNveAsk(false);
      showToast(`${name} added.`);
    } catch (e) { showToast(e?.error || 'Could not add that workflow.'); }
  }

  // Two different things wear the same button. A workflow with a card behind it is
  // DELETED — the card goes, and the server refuses if there's anything in it.
  // Everything else is HIDDEN: the pillow comes off this job's page and the data it
  // was showing is untouched, so adding it back brings everything with it.
  async function removePillow(p, sj) {
    const workflowCard = !!(p.sub && sj && !CARDLESS.includes(p.key));
    const what = workflowCard ? `Remove ${p.label} from this job?`
      : `Take ${p.label} off this job's page? Nothing in it is deleted — you can add it back.`;
    if (!window.confirm(what)) return;
    try {
      if (!workflowCard) {
        await apiFetch(`/projects/${id}/hidden-pillows`, {
          method: 'POST', body: JSON.stringify({ pillow_key: p.key }),
        });
        setHidden(h => (h.includes(p.key) ? h : [...h, p.key]));
      } else {
        await apiFetch(`/projects/${id}/sub-jobs/${sj.id}?emptyOnly=1`, { method: 'DELETE' });
        await reloadSubJobs();
      }
      showToast(`${p.label} removed.`);
    } catch (e) { showToast(e?.error || 'Could not remove that.'); }
  }


  const pmNameFor = pid => {
    if (!pid) return 'Unassigned';
    const e = employees.find(x => String(x.id) === String(pid));
    if (!e) return 'Unassigned';
    return `${e.first_name || e.firstName || ''} ${e.last_name || e.lastName || ''}`.trim() || 'Unassigned';
  };

  async function reloadJob() {
    try {
      const [p, b] = await Promise.all([
        apiFetch(`/projects/${id}`),
        bid?.id ? apiFetch(`/bids/${bid.id}`) : Promise.resolve(null),
      ]);
      if (p) { setProject(p); setSubJobs(Array.isArray(p.subJobs) ? p.subJobs : []); }
      if (b) setBid(prev => ({ ...prev, ...b }));
      // The bid carries its line items — an import from the old quote lands here.
      if (b && Array.isArray(b.items)) setItems(b.items);
    } catch { /* the screen keeps what it has */ }
    loadFwPins();
  }

  async function reloadSubJobs() {
    try {
      const p = await apiFetch(`/projects/${id}`);
      setSubJobs(Array.isArray(p?.subJobs) ? p.subJobs : []);
      setDrillCardSj(sj => (sj ? (p.subJobs || []).find(x => x.id === sj.id) || sj : sj));
    } catch { /* the card keeps what it has */ }
    loadFwPins();
  }
  function loadFwPins() {
    apiFetch(`/projects/${id}/fieldwire`).then(ov => {
      const m = {};
      if (ov?.linked) for (const t of ov.teams || []) if (t.sub_job_id) {
        m[t.sub_job_id] = { tasks: (m[t.sub_job_id]?.tasks || 0) + (t.tasks || 0), done: (m[t.sub_job_id]?.done || 0) + (t.done || 0) };
      }
      setFwPins(m);
    }).catch(() => setFwPins({}));
  }
  useEffect(() => { loadFwPins(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // What each tile says on its face. Counts it doesn't have yet just show the number
  // of cards, rather than a figure that pops in and changes under you.
  function summaryFor(key, live) {
    const cards = `${live} ${live === 1 ? 'card' : 'cards'}`;
    if (key === 'work_flows') return live ? `${live} workflows running` : 'No workflows on this job';
    if (key === 'material')   return counts.pos == null ? cards : `${counts.pos} purchase order${counts.pos === 1 ? '' : 's'}`;
    if (key === 'plans')      return counts.plans == null ? cards : (counts.plans ? `${counts.plans} document${counts.plans === 1 ? '' : 's'}` : 'Nothing uploaded yet');
    if (key === 'job_info')   return items.length ? `${items.length} line items` : cards;
    if (key === 'schedule') {
      if (counts.crew == null) return cards;
      return counts.crew ? `${counts.crew} on the schedule this week` : 'Nobody scheduled this week';
    }
    return cards;
  }

  const Wrap = embedded ? MobileBidShell : AdminLayout;
  // On a phone the shell's title bar is the only thing above the fold that says
  // where you are, so it says the job rather than "Bid Details".
  const wrapProps = embedded
    ? { title: project?.project_number ? `Job ${project.project_number}` : 'Job', bottomNav: true }
    : {};
  if (loading) return <Wrap onBack={() => goBack(navigate, '/admin/new-projects')} {...wrapProps}><div style={{ padding: 40, color: '#6b7280' }}>Loading job…</div></Wrap>;
  if (error) return (
    <Wrap onBack={() => goBack(navigate, '/admin/new-projects')} {...wrapProps}>
      <div style={{ padding: 40 }}>
        <div style={{ color: '#b91c1c', marginBottom: 14 }}>{error}</div>
        <button onClick={() => navigate(embedded ? `/sup/old-job/${id}` : `/admin/old-job/${id}`)}
          style={{ ...selS, padding: '8px 14px', fontSize: 13, fontWeight: 700 }}>
          Open the current job page instead
        </button>
      </div>
    </Wrap>
  );

  // The top Back goes to the page you came from — the Big Picture, a dashboard, the Projects list
  // (Ryan, Nov 1 2026: "take you to the last page you were at") — and to the Projects list when the job
  // was opened cold, from a link or a bell (Pat, Oct 6 2026). Inside a tile, "‹ Back to all tiles" under
  // the banner takes you to the job's six tiles instead. The phone keeps going back where you came from.
  return (
    <Wrap onBack={() => goBack(navigate, embedded ? null : '/admin/new-projects')} {...wrapProps}>
      <div style={{ maxWidth: 1100 }}>
        {/* The same banner the current job page uses — one shared component, so the
            two can't drift. Edit Project, the quote PDF and Delete still live on that
            page, so those send you across rather than being rebuilt here. */}
        <JobBanner
          project={project} bid={bid} projectId={id}
          statusMeta={statusMeta} addressee={addressee}
          currentUserRole={role} isLocked={bid?.status === 'awarded'}
          onStatusChange={async next => {
            if (next === project?.status) return;
            if (next === 'closed' && !window.confirm(`Close ${project?.project_number || 'this job'}? It comes off the active lists, the field's job pickers and the dashboards. You can reopen it here any time.`)) return;
            try {
              await apiFetch(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
              showToast(next === 'active' ? 'Job reopened' : next === 'complete' ? 'Job marked complete' : 'Job closed');
              reloadJob();
            } catch (e) { showToast(e?.error || 'Could not change the status.', 'error'); }
          }}
          onBack={() => navigate(tileKey ? hubUrl : '/admin/new-projects')}
          onEditProject={embedded ? null : () => navigate(`/admin/old-job/${id}`)}
          onDelete={embedded ? null : () => navigate(`/admin/old-job/${id}`)}
          onWritePO={() => setShowPo(true)}
          onQuotePdf={embedded ? null : async () => {
            // The quote the job was won on, saved in Documents as Used Quote (Ryan, Oct 22 2026) —
            // in a window over the job page, not a new tab (Ryan, Oct 26 2026).
            try {
              const r = await apiFetch(`/projects/${id}/used-quote`);
              setUsedQuote(r.document);
            } catch (e) { window.alert(e?.error || e?.message || 'Could not find the used quote.'); }
          }}
          onLocation={() => setShowLocation(true)}
          onScheduleWorkflow={isAdminZone ? setScheduleWorkflow : null}
          onTakeToCard={project?.division === 'small' ? () => navigate(`/admin/workflows/${project.schedule_workflow || 'small_jobs'}?card=${id}`) : null}
          shareable
        />
        {sameNumber && (
          <div style={{ margin: '10px 0', padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13.5, fontWeight: 700 }}>
            ⚠ Another job has this same number: <button onClick={() => navigate(`/admin/jobs/${sameNumber.id}`)} style={{ background: 'none', border: 'none', padding: 0, color: '#991b1b', textDecoration: 'underline', fontWeight: 800, cursor: 'pointer', font: 'inherit' }}>{sameNumber.project_number} {sameNumber.name}</button>
            <span style={{ fontWeight: 500 }}> — Fieldwire, QuickBooks Time and lookups by number can land on the wrong one. Renumber one of them.</span>
          </div>
        )}

        {/* Jobs open here now. The old page is still one tap away for anything this
            one can't do yet — and for anyone who just wants what they know. */}
        <div style={{ display: 'flex', justifyContent: tileKey ? 'space-between' : 'flex-end', alignItems: 'center', marginTop: 10 }}>
          {tileKey && (
            <button onClick={() => navigate(hubUrl)}
              style={{ padding: '7px 14px', borderRadius: 999, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                fontSize: 13, fontWeight: 800, color: '#1e3a5f' }}>
              ‹ Back to all tiles
            </button>
          )}
          <button onClick={() => navigate(embedded ? `/sup/old-job/${id}` : `/admin/old-job/${id}`)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 700, color: '#9ca3af', textDecoration: 'underline' }}>
            Open the old job page
          </button>
        </div>

        {/* New job: the PM picks its workflows the first time they open it (Pat, Oct 6 2026). */}
        {!embedded && project && project.workflows_setup_pending ? (
          <WorkflowSetupCard project={project} subJobs={subJobs} onDone={async () => { await reloadSubJobs(); reloadJob(); }} showToast={showToast} />
        ) : null}

        {canArrange && (
          <div style={{ ...card, borderColor: '#fed7aa', background: '#fff7ed', padding: '12px 16px', margin: '14px 0 20px', fontSize: 13, color: '#7c2d12' }}>
            <b>Arranging.</b> The two pickers on each pillow set which tile it belongs to and who
            sees it. Changes apply to <b>every job</b>, straight away. Only you see these pickers.
          </div>
        )}

        {!tileKey ? (
          /* ── The hub: five tiles. Click one to go into it. ───────────────── */
          <div style={{ display: 'grid', gridTemplateColumns: embedded ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
            {TILES.map(t => {
              const rows = byTile[t.key] || [];
              const live = rows.filter(r => !r.missing).length;
              return (
                <button key={t.key} onClick={() => navigate(tileUrl(t.key))}
                  style={{ ...card, textAlign: 'left', cursor: 'pointer', padding: '18px 20px', font: 'inherit',
                    display: 'flex', flexDirection: 'column', gap: 6, minHeight: 128 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#e87722'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111', flex: 1 }}>{t.label}</h2>
                    <span style={{ color: '#e87722', fontSize: 18, lineHeight: 1 }}>›</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#9ca3af', lineHeight: 1.45 }}>{t.blurb}</div>
                  <div style={{ marginTop: 'auto', paddingTop: 8, fontSize: 13, color: '#374151', fontWeight: 600 }}>
                    {summaryFor(t.key, live)}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          /* ── Inside one tile: its pillows as cards, not rows ─────────────── */
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 12px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111' }}>{activeTile?.label}</h2>
              {!embedded && <span style={{ fontSize: 12.5, color: '#9ca3af', flex: 1 }}>{activeTile?.blurb}</span>}
              {embedded && <span style={{ flex: 1 }} />}
              {/* Adding a workflow is the first thing on the board, not the last tile
                  after Punch List — you shouldn't have to scroll past the job to add to it. */}
              {tileKey === 'work_flows' && isAdminZone && (
                <button onClick={() => setAddOpen(true)}
                  style={{ padding: '6px 14px', borderRadius: 999, border: '1px solid #fed7aa',
                    background: '#fff7ed', color: '#c2410c', fontSize: 12.5, fontWeight: 700,
                    cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  + Add a workflow
                </button>
              )}
              <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {tileRows.length === 0 ? 'nothing on this job' : `${tileRows.length} ${tileRows.length === 1 ? 'pillow' : 'pillows'}`}
              </span>
            </div>

            {tileRows.length === 0 ? (
              <div style={{ ...card, padding: '16px 18px', fontSize: 13, color: '#9ca3af' }}>Nothing here on this job yet.</div>
            ) : (
              <div className={embedded ? undefined : 'tile-grid-3'} style={{ ...(embedded ? { display: 'grid', gridTemplateColumns: '1fr' } : null), gap: 12, alignItems: 'stretch' }}>
                {tileRows.map(p => (
                  <React.Fragment key={p.key}>
                  <Pillow p={p} projectId={id} project={project} bid={bid} items={items}
                    onOpenItem={openPillowItem} fwPins={fwPins}
                    subJobs={subJobs} subJobByType={subJobByType} expanded={expandedKey === p.key}
                    onExpand={() => openPillowKey(expandedKey === p.key ? null : p.key)}
                    onOpenCard={setOpenCard} canArrange={canArrange} onMove={move}
                    onRemove={removePillow} pmName={pmNameFor} onChanged={reloadSubJobs}
                    onAddCard={addWorkflowCard} height={pillowHeight(tileRows.length, embedded)}
                    isAdminZone={isAdminZone} isExec={isExec} role={role} onJobChanged={reloadJob}
                    showToast={showToast} navigate={navigate} embedded={embedded} />
                  {/* Opened, a pillow comes up front and centre in a window over the page —
                      the tile stays where it is in the grid. (For a day it opened in a row
                      under its pillow like the workflow cards; Ryan wanted the window back
                      for these big tiles, Oct 2 2026.) Submittals take the whole screen. */}
                  {expandedKey === p.key && (() => {
                    const sj = p.sub ? subJobByType[p.sub] : null;
                    const full = FULL_SCREEN_PILLOWS.includes(p.key);
                    // A workflow pillow opens THE card — the same window the Workflow board opens,
                    // front and centre, not a copy inside a generic window (Ryan, Oct 2 2026).
                    if (p.opensCard && sj) return <WorkflowCardModal kind={p.opensCard} sj={sj} project={project} pmName={pmNameFor} onChanged={reloadSubJobs} onClose={() => openPillowKey(null)} item={null} />;
                    return (
                      <div id={'pillow-open-' + p.key} onClick={full ? undefined : () => openPillowKey(null)} style={full
                        ? { position: 'fixed', inset: 0, zIndex: 3500, background: '#f8fafc', overflowY: 'auto', display: 'flex', flexDirection: 'column' }
                        : { position: 'fixed', inset: 0, zIndex: 3500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 20px', overflowY: 'auto' }}>
                      {/* Wide enough for three fields across and tall enough that a short
                          pillow doesn't read as a strip. */}
                      <div onClick={e => e.stopPropagation()} style={full
                        ? { display: 'flex', flexDirection: 'column', flex: 1 }
                        : { background: '#fff', borderRadius: 14, width: 1240, maxWidth: '100%', minHeight: '62vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f1f1', display: 'flex', alignItems: 'center', gap: 12, ...(full ? { position: 'sticky', top: 0, zIndex: 2, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' } : {}) }}>
                          {full && <button onClick={() => openPillowKey(null)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>‹ Back to the job</button>}
                          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, flex: 1 }}>{full && project && project.project_number && !String(project.name || '').startsWith(String(project.project_number)) ? `${project.project_number} · ` : ''}{p.label}{full && project?.name ? <span style={{ fontWeight: 500, color: '#6b7280', fontSize: 14 }}> — {project.name}</span> : null}</h3>
                          {/* The open pillow is in the address, so forwarding sends exactly this. */}
                          <SendPointer label={`${project?.project_number || ''} ${p.label}`.trim()} />
                          <button onClick={() => openPillowKey(null)} title="Close"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
                        </div>
                        <div style={{ padding: 18 }}>
                          {p.opensCard && sj
                            ? <WorkflowCardInline kind={p.opensCard} sj={sj} project={project} pmName={pmNameFor} onChanged={reloadSubJobs} />
                            : LIST_IS_THE_FULL_VIEW.includes(p.key) && hasItemSource(p.key)
                            ? <PillowItemList pillowKey={p.key} projectId={id}
                                subJobId={sj?.id} subJobs={p.sub ? subJobs.filter(x => x.type === p.sub) : []}
                                canSeeMoney={isAdminZone} openItemId={wantItem}
                                onOpen={it => { if (p.opensCard && sj) setOpenCard({ kind: p.opensCard, sj, item: it }); }} />
                            : <PillowBody pillow={p} projectId={id} project={project} bid={bid} items={items}
                                sj={sj} embedded={embedded} isAdminZone={isAdminZone} role={role} showToast={showToast} navigate={navigate} isExec={isExec} onJobChanged={reloadJob} expanded
                                openItemId={wantItem} />}
                        </div>
                      </div>
                      </div>
                    );
                  })()}
                  </React.Fragment>
                ))}

                {/* Anything taken off this tile, offered back. Work Flows has its own
                    picker for adding a card that doesn't exist yet; this puts back a
                    pillow that was hidden, with everything that was in it. */}
                {isAdminZone && hiddenFor(tileKey).length > 0 && (
                  <button onClick={() => setAddBackOpen(true)}
                    style={{ ...card, height: pillowHeight(tileRows.length, embedded) || 300,
                      border: '2px dashed #e5e7eb', background: '#fcfcfc',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 6, cursor: 'pointer', font: 'inherit', color: '#9ca3af' }}>
                    <span style={{ fontSize: 24, lineHeight: 1, color: '#c2410c' }}>+</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#c2410c', textAlign: 'center', padding: '0 10px' }}>
                      {hiddenFor(tileKey).length === 1
                        ? `Add back ${hiddenFor(tileKey)[0].label}`
                        : `Add back one of ${hiddenFor(tileKey).length}`}
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* The opened pillow gets the full width underneath, so a wide thing like
                the PO list or the plans isn't squeezed into one grid column. */}
            {openPillow && (
              <section style={{ ...card, marginTop: 14 }}>
                <div style={{ padding: '13px 18px', borderBottom: '1px solid #f1f1f1', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111', flex: 1 }}>{openPillow.label}</h3>
                  <button onClick={() => setOpenKey(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, lineHeight: 1, padding: 0 }}
                    title="Close">×</button>
                </div>
                <div style={{ padding: '14px 18px 18px' }}>
                  {openPillow.missing ? (
                    <Stub text="This job doesn't have this workflow — you're only setting where it will sit when a job does." />
                  ) : LIST_IS_THE_FULL_VIEW.includes(openPillow.key) && hasItemSource(openPillow.key) ? (
                    <PillowItemList pillowKey={openPillow.key} projectId={id}
                      subJobId={openPillow.sub ? subJobByType[openPillow.sub]?.id : null}
                      subJobs={openPillow.sub ? subJobs.filter(x => x.type === openPillow.sub) : []}
                      canSeeMoney={isAdminZone} openItemId={wantItem}
                      onOpen={(it, i) => { const sj = openPillow.sub ? subJobByType[openPillow.sub] : null;
                        if (openPillow.opensCard && sj) setOpenCard({ kind: openPillow.opensCard, sj, item: it }); }} />
                  ) : (
                    <PillowBody
                      pillow={openPillow} projectId={id} project={project} bid={bid} items={items}
                      sj={openPillow.sub ? subJobByType[openPillow.sub] : null} embedded={embedded} isExec={isExec} onJobChanged={reloadJob} expanded
                      isAdminZone={isAdminZone} role={role} showToast={showToast} navigate={navigate} openItemId={wantItem}
                    />
                  )}
                </div>
              </section>
            )}
          </>
        )}


        {addBackOpen && (
          <div onClick={() => setAddBackOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 5200,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '100%', padding: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Add back to {activeTile?.label}</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 14 }}>
                Taken off this job's page. Nothing in them was deleted — put one back and it returns as it was.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {hiddenFor(tileKey).map(p => (
                  <button key={p.key} onClick={() => unhidePillow(p)}
                    style={{ padding: '10px 12px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff',
                      textAlign: 'left', font: 'inherit', fontSize: 13.5, fontWeight: 700, color: '#111', cursor: 'pointer' }}>
                    {p.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setAddBackOpen(false)}
                style={{ marginTop: 16, width: '100%', padding: '10px', borderRadius: 9, border: '1px solid #e5e7eb',
                  background: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {addOpen && (
          <div onClick={() => setAddOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 5200,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, width: 520, maxWidth: '100%', padding: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Add a workflow</div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 14 }}>
                Only what this job needs — you can remove one again while it's still empty.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
                {WORKFLOW_CHOICES.map(c => {
                  // Punch List and Work Orders read the job's own punch items and work
                  // orders rather than a card, so "on this job" for them means the tab
                  // hasn't been taken off — not that a sub-job row exists.
                  const already = CARDLESS.includes(c.key)
                    ? !hidden.includes(c.key)
                    : subJobs.some(x => x.type === c.key);
                  return (
                    <button key={c.key} disabled={already} onClick={() => addWorkflow(c)}
                      style={{ padding: '12px 8px', borderRadius: 10, textAlign: 'center', font: 'inherit',
                        border: '2px solid ' + (already ? '#f3f4f6' : '#e5e7eb'),
                        background: already ? '#f9fafb' : '#fff',
                        cursor: already ? 'default' : 'pointer', opacity: already ? 0.55 : 1 }}>
                      <div style={{ fontSize: 20 }}>{c.icon}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 4, color: '#111' }}>{c.label}</div>
                      {already && (
                        <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 700, marginTop: 2 }}>✓ on this job</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {nveAsk && (
                <div style={{ marginTop: 14, padding: 12, borderRadius: 12, border: '2px solid #fde68a', background: '#fffbeb' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#92400e', marginBottom: 8 }}>⚡ NVE — what kind?</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                    {[
                      { icon: '⚡', label: 'New Service', sub: 'Meters, permits, inspections', extra: { nve_pole_hold: false, nve_name: 'NVE New Service' } },
                      { icon: '⬇️', label: 'Underground only', sub: 'No meters — goes by the PID', extra: { nve_pole_hold: false, nve_underground: true, nve_name: 'NVE Underground' } },
                      { icon: '🪝', label: 'Pole Hold', sub: "Request it from NV Energy at the job's address", extra: { nve_pole_hold: true, nve_name: 'NVE Pole Hold' } },
                    ].map(k => (
                      <button key={k.label} onClick={() => addWorkflow(nveAsk, k.extra)} style={{ padding: '12px 8px', borderRadius: 10, border: '2px solid #e5e7eb', background: '#fff', cursor: 'pointer', font: 'inherit' }}>
                        <div style={{ fontSize: 20 }}>{k.icon}</div><div style={{ fontSize: 13, fontWeight: 800 }}>{k.label}</div><div style={{ fontSize: 11.5, color: '#6b7280' }}>{k.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => { setAddOpen(false); setNveAsk(false); }}
                style={{ marginTop: 16, width: '100%', padding: '10px', borderRadius: 9, border: '1px solid #e5e7eb',
                  background: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Clicking an item opens its workflow's own card, centred. */}
        {openCard && (
          <WorkflowCardModal kind={openCard.kind} sj={openCard.sj} item={openCard.item} project={project}
            pmName={pmNameFor} onChanged={reloadSubJobs} onClose={() => setOpenCard(null)} />
        )}

        {/* The Vertical Drilling card itself, opened from its pillow. */}
        {drillCardSj && (
          <DrillCardModal
            card={workflowCardFromSubJob(drillCardSj, project)}
            pmName={pmNameFor}
            onClose={() => setDrillCardSj(null)}
            onChanged={reloadSubJobs}
          />
        )}

        {usedQuote && (
          <PdfWindow fetchPath={`/documents/${usedQuote.id}/file`} title={usedQuote.title || 'Used Quote'}
            fileName={`${project?.project_number || ''} Used Quote`.trim()} onClose={() => setUsedQuote(null)} />
        )}
        {showLocation && (
          <JobLocationModal projectId={id} address={bid?.job_location}
            label={project?.project_number || project?.name || 'Job Location'}
            onPick={() => { setShowLocation(false); setPickPin(true); }} refreshKey={pinKey} qbTime
            onClose={() => setShowLocation(false)} />
        )}
        {pickPin && (
          <LocationPickerModal value={bid?.job_location || ''}
            onConfirm={async ({ address, lat, lng }) => {
              try {
                // The job's point (the server copies the pin onto its bid too, so the two agree).
                await apiFetch(`/projects/${id}/locations`, { method: 'POST', body: JSON.stringify({ address: address || bid?.job_location || `${lat}, ${lng}`, lat: lat ?? null, lng: lng ?? null, replace: true }) });
                setPinKey(k => k + 1);
              } catch (e) { window.alert(e?.error || e?.message || 'Could not save the pin.'); }
              setShowLocation(true);
            }}
            onClose={() => { setPickPin(false); setShowLocation(true); }} />
        )}

        <CreatePoModal
          open={showPo} onClose={() => setShowPo(false)}
          projectId={id} projectLabel={`${project?.project_number || ''} ${jobName(project) || ''}`.trim()}
          projects={project ? [project] : []} vendors={vendors}
          onCreated={po => showToast(`PO #${po?.po_number} created.`)}
        />

        {toast && (
          <div style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', background: '#111', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: 13, zIndex: 9000 }}>
            {toast}
          </div>
        )}
      </div>
    </Wrap>
  );
}

// Vendor Quotes on the job page: the bid page's section, fed the bid's vendor_quotes
// JSON and its attachments, saving straight back to the bid. One source of truth, so
// what the estimator entered on the bid is what the PM sees on the job.
function VendorQuotesPillow({ bid, projectId, showToast }) {
  const [vq, setVq] = useState(bid?.vendor_quotes || null);
  const [attachments, setAttachments] = useState([]);
  useEffect(() => { setVq(bid?.vendor_quotes || null); }, [bid?.id, bid?.vendor_quotes]);
  useEffect(() => {
    if (!bid?.id) return undefined;
    let live = true;
    apiFetch(`/bids/${bid.id}/attachments`).then(r => { if (live) setAttachments(Array.isArray(r) ? r : []); }).catch(() => {});
    return () => { live = false; };
  }, [bid?.id]);
  if (!bid?.id) return <Stub text="This job has no bid behind it, so there are no vendor quotes to show." />;

  async function save(items) {
    const json = JSON.stringify(items);
    setVq(json);
    try {
      await apiFetch(`/bids/${bid.id}`, { method: 'PATCH', body: JSON.stringify({ vendor_quotes: json }) });
      // The Documents pillow files a quote's PDF under Vendor Quotes once it's referenced.
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId, bidId: bid.id } }));
    } catch (e) { showToast && showToast(e?.error || e?.message || 'Could not save the vendor quotes.'); }
  }
  return (
    <VendorQuotesSection
      bid={{ ...bid, vendor_quotes: vq }}
      isLocked={false}
      show
      readFirst
      onToggle={() => {}}
      attachments={attachments}
      onQuotesChange={save}
      onFileUpload={async (file, quoteId, currentItems) => {
        const fd = new FormData(); fd.append('file', file);
        const res = await fetch(`${API_BASE}/bids/${bid.id}/attachments`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
        if (!res.ok) { showToast && showToast('Could not upload that file.'); return null; }
        const att = await res.json();
        setAttachments(a => [...a, att]);
        await save(currentItems.map(it => ({ ...it, quotes: (it.quotes || []).map(q => q.id === quoteId ? { ...q, files: [...(q.files || []), att.id] } : q) })));
        return att;
      }}
      onFileRemove={(attId, quoteId, currentItems) => save(currentItems.map(it => ({ ...it, quotes: (it.quotes || []).map(q => q.id === quoteId ? { ...q, files: (q.files || []).filter(f => f !== attId) } : q) })))}
      getToken={getToken}
      API_BASE={API_BASE}
      bidId={bid.id}
      projectId={projectId}
      onPOCreated={po => showToast && showToast(`PO #${po?.po_number} created.`)}
      showToast={showToast}
    />
  );
}

// Renders a pillow's real contents. Where a pillow is already its own component it is
// imported from the old page and used as-is — same code, same behaviour, no fork.
// The rest are still inline in that 10,000-line file; they say so plainly and link to
// where they live, rather than pretending to be built.
function PillowBody({ pillow, projectId, project, bid, items, sj, isAdminZone, isExec, role, onJobChanged, showToast, navigate, embedded, expanded = false, openItemId, onOpenItem }) {
  const shared = { API_BASE, getToken, showToast };

  switch (pillow.key) {
    // Fieldwire pins whose category is mapped to this card sit above the tab (quiet when none).
    case 'usa_digs':      return <><FieldwireTasks projectId={projectId} subJobId={sj.id} canEdit compact /><UsaDigsTab sjId={sj.id} isAdminZone={isAdminZone} {...shared} /></>;
    case 'nve':           return <><FieldwireTasks projectId={projectId} subJobId={sj.id} canEdit compact /><NVETab sjId={sj.id} isAdminZone={isAdminZone} {...shared} /></>;
    case 'loops':         return <><FieldwireTasks projectId={projectId} subJobId={sj.id} canEdit compact /><LoopsTab sjId={sj.id} isAdminZone={isAdminZone} {...shared} /></>;
    case 'shop':          return <ShopTab projectId={projectId} sjId={sj.id} isAdminZone={isAdminZone} {...shared} />;
    case 'payroll':       return <PayrollTab sjId={sj.id} isAdminZone={isAdminZone} {...shared} />;
    case 'submittals':    return <SubmittalsTab sjId={sj.id} projectId={projectId} project={project} bidItems={items} isAdminZone={isAdminZone} openItemId={openItemId} {...shared} />;
    case 'extra_work':    return <ExtraWorkDaily sjId={sj.id} projectId={projectId} projectNumber={project?.project_number} canEdit={isAdminZone || role === 'FieldSupervisor'} canSeeMoney={isAdminZone} />;
    case 'change_orders': return <ExtraWorkTab sjId={sj.id} projectId={projectId} project={project} isAdminZone={isAdminZone} bid={bid} isChangeOrders openItemId={openItemId} {...shared} />;
    // The same Vendor Quotes the bid page and the old job page show — it was a
    // "not moved yet" placeholder here (Chad, 2631, Sep 2026).
    // This job's shop orders — the same board the Shop page shows, filtered to the job,
    // with Create Order landing on it. Was a "not moved yet" placeholder (Sep 2026).
    case 'shop_order':      return <ShopOrdersBoard projectId={projectId} openItemId={openItemId} />;
    // The bid's cost stack and the bid record itself — both were "not moved yet"
    // placeholders (Sep 2026). Money is office-side.
    case 'tsheets':         return isAdminZone ? <TsheetsPillow project={project} expanded={expanded} /> : <Stub text="Hours by person are office-side." />;
    // The crews' Fieldwire pins, by category, mapped onto the job's workflow cards.
    case 'fieldwire':       return <FieldwirePillow projectId={projectId} isAdminZone={isAdminZone} isExec={isExec} expanded={expanded} showToast={showToast} onWorkflowsChanged={onJobChanged} />;
    case 'fieldwire_sheets': return <FieldwireSheetsPillow projectId={projectId} isAdminZone={isAdminZone} expanded={expanded} />;
    case 'vendor_quotes':   return isAdminZone
      ? <VendorQuotesPillow bid={bid} projectId={projectId} showToast={showToast} />
      : <Stub text="Vendor quotes are office-side." />;
    case 'equipment_on_job': return <EquipmentJobCard projectId={projectId} />;
    // The window shows the list; opened out it's the whole conversation.
    // Collapsed, a tapped RFI opens the centred panel on that RFI. Expanded, it is
    // already centred and opens in place.
    case 'rfi':             return <RfiPillow projectId={projectId} project={project} bid={bid} canEdit={isAdminZone} compact={!expanded} openItemId={openItemId} onOpenItem={!expanded && onOpenItem ? (id => onOpenItem('rfi', id)) : null} />;
    case 'tm_pricing':      return <TmTile projectId={projectId} bid={bid} project={project} canEdit={isAdminZone} compact={!expanded} />;
    case 'tm_billing':
      // Small-job work orders bill T&M by the day — the same daily tracker as Extra Work.
      if (project?.schedule_workflow === 'small_jobs')
        return <TmProjectTracker projectId={projectId} projectNumber={project?.project_number} canEdit={isAdminZone || role === 'FieldSupervisor'} canSeeMoney={isAdminZone} workflow="small_jobs" rateSource={project?.tm_rate_source || null} />;
      // Every other job keeps the labour/material/equipment line items when it's T&M.
      return bid?.pricing_type === 'tm'
        ? <TMBillingSection projectId={projectId} bid={bid} />
        : <Stub text="This job isn’t Time & Material, so there’s nothing to bill here." />;

    // Pulled out of the old page into JobPillows.js — same endpoints, own components.
    // Only an Executive edits the job's facts; everyone else reads them.
    // In its window it's a summary worth scanning; opened out it's the form.
    case 'job_header':      return <JobHeaderPillow project={project} bid={bid} canEdit={isAdminZone} compact={!expanded} onChanged={onJobChanged} />;
    case 'bid_notes':       return <NotesPillow projectId={projectId} />;
    case 'job_photos':      return <JobPhotosPillow projectId={projectId} />;
    case 'daily_updates':   return <DailyUpdatesPillow projectId={projectId} />;
    case 'job_schedule':    return <SchedulePillow projectId={projectId} />;
    // Only the office sets the plan; the field reads it. Zone controls who sees the
    // pillow at all, this controls who can move a date once they're looking at it.
    case 'big_picture':     return <BigPicturePillow projectId={projectId} canEdit={isAdminZone} />;
    case 'wo_billing':      return <WoBillingPillow projectId={projectId} compact={!expanded} />;
    case 'punch_list':      return <PunchListPillow projectId={projectId} />;
    case 'close_out':       return <CloseOutPillow projectId={projectId} />;
    case 'work_orders':     return <WorkOrdersPillow projectId={projectId} navigate={navigate} embedded={embedded} />;
    case 'purchase_orders': return <PurchaseOrdersPillow projectId={projectId} canSeeMoney={isAdminZone} />;
    case 'permits':         return isAdminZone ? <JobPermitsPillow projectId={projectId} /> : <div style={{ fontSize: 13, color: '#9ca3af' }}>The office keeps the permits.</div>;
    // bidId = the project id: the Fieldwire plans strip works off either (Ryan, Oct 3 2026), so a
    // job with no bid (a work order) can still send its plans up and make the Fieldwire project.
    case 'documents':       return <DocumentsPillow projectId={projectId} fieldwireId={projectId} canEdit={isAdminZone} compact={!expanded} />;
    case 'bid_recipients':  return <RecipientsPillow bid={bid} />;
    case 'line_items':      return <LineItemsPillow items={items} bid={bid} canSeeMoney={isAdminZone} projectId={projectId} navigate={navigate} embedded={embedded} onImported={onJobChanged} />;
    case 'contract':
    case 'subcontracts':    return <NotesOnlyPillow projectId={projectId} sj={sj} canEdit={isAdminZone} />;

    default:
      return <NotYetMoved label={pillow.label} projectId={projectId} navigate={navigate} embedded={embedded} />;
  }
}

// One pillow: a window with its count at the top and its cards scrolling inside.
// Clicking a card opens it centred; "see all" expands the pillow to three across,
// so a busy workflow can be read without scrolling a small box.
// "Which workflows does this job need?" — once, for the job's PM (or, with no PM on it, the
// office / Executives), on a job just awarded with only the bid's workflows. Ticked ones are
// added as cards; "Done" (with or without any) stops it asking. "Later" hides it for now.
const SETUP_CHOICES = [{ key: 'contract', label: 'Contract', icon: '📄' }, ...WORKFLOW_CHOICES.filter(c => !['custom', 'punch_list', 'work_orders'].includes(c.key))];
function WorkflowSetupCard({ project, subJobs, onDone, showToast }) {
  const me = (() => { try { const x = JSON.parse(localStorage.getItem('titan_session') || 'null'); return (x && ((x.user && x.user.id) || x.userId)) || null; } catch { return null; } })();
  const myRole = (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || ''; } catch { return ''; } })();
  const forMe = project.pm_id ? String(project.pm_id) === String(me) : ['admin', 'Admin', 'Executive', 'Accounting', 'OfficeStaff'].includes(myRole);
  const have = new Set((subJobs || []).map(x => x.type));
  const [pick, setPick] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [later, setLater] = useState(() => { try { return sessionStorage.getItem('wf_setup_later_' + project.id) === '1'; } catch { return false; } });
  if (!forMe || later) return null;
  async function finish(addThese) {
    setBusy(true);
    try {
      for (const k of addThese) {
        const c = SETUP_CHOICES.find(x => x.key === k);
        await apiFetch(`/projects/${project.id}/sub-jobs`, { method: 'POST', body: JSON.stringify({ type: k, name: c ? c.label : k }) });
      }
      await apiFetch(`/projects/${project.id}/workflows-setup-done`, { method: 'POST', body: JSON.stringify({ added: addThese.map(k => (SETUP_CHOICES.find(x => x.key === k) || {}).label || k) }) });
      showToast && showToast(addThese.length ? `Added ${addThese.length} workflow${addThese.length === 1 ? '' : 's'}.` : 'Got it — no more workflows.');
      await onDone();
    } catch (e) { showToast && showToast(e?.error || 'Could not add those.'); }
    setBusy(false);
  }
  return (
    <div style={{ ...card, borderColor: '#bfdbfe', background: '#eff6ff', padding: '14px 16px', margin: '14px 0 6px' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#1e3a8a' }}>👋 New job — which workflows does it need?</div>
      <div style={{ fontSize: 12.5, color: '#475569', margin: '3px 0 10px' }}>
        It came over with the bid’s workflows{have.size ? <> (<b>{(subJobs || []).map(x => x.name || x.type).join(', ')}</b>)</> : ' (none)'}. Tick anything else it needs — you can still add more later with “+ Add a workflow”.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {SETUP_CHOICES.map(c => {
          const on = have.has(c.key), ticked = pick.has(c.key);
          return (
            <button key={c.key} type="button" disabled={on || busy}
              onClick={() => setPick(prev => { const n = new Set(prev); n.has(c.key) ? n.delete(c.key) : n.add(c.key); return n; })}
              style={{ padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: on ? 'default' : 'pointer',
                border: `1.5px solid ${on ? '#bbf7d0' : ticked ? '#2563eb' : '#cbd5e1'}`, background: on ? '#f0fdf4' : ticked ? '#dbeafe' : '#fff', color: on ? '#15803d' : ticked ? '#1d4ed8' : '#334155' }}>
              {on ? '✓ ' : ticked ? '☑ ' : '☐ '}{c.icon} {c.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" disabled={busy} onClick={() => finish([...pick])}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          {busy ? 'Adding…' : pick.size ? `Add ${pick.size} and finish` : 'Nothing more — finish'}
        </button>
        <button type="button" disabled={busy} onClick={() => { try { sessionStorage.setItem('wf_setup_later_' + project.id, '1'); } catch { /* fine */ } setLater(true); }}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer' }}>Later</button>
      </div>
    </div>
  );
}

function Pillow({ p, projectId, project, bid, items, subJobs, subJobByType, expanded, onExpand,
                  onOpenCard, onOpenItem, canArrange, onMove, onRemove, pmName, onChanged, onAddCard, height,
                  isAdminZone, isExec, role, onJobChanged, showToast, navigate, embedded, fwPins }) {
  const mine = p.sub ? (subJobs || []).filter(x => x.type === p.sub) : [];
  const sj = mine[0] || null;
  // Fieldwire pins mapped to any of this pillow's cards — shown on the face of the pillow.
  const fw = mine.reduce((a, x) => { const f = fwPins?.[x.id]; return f ? { tasks: a.tasks + f.tasks, done: a.done + f.done } : a; }, { tasks: 0, done: 0 });
  const [reloadKey, setReloadKey] = useState(0);
  const [sortKey, setSortKey] = useState(null);   // null = the source's own order
  const [sortFlip, setSortFlip] = useState(false);
  const { items: rows, source } = usePillowItems(p.key, { projectId, subJobId: sj?.id, subJobs: mine, reloadKey });
  const zs = ZONE_STYLE[p.zone] || ZONE_STYLE.both;
  const n = rows == null ? null : rows.length;
  const shown = source?.sorts && sortKey ? sortItems(rows, source.sorts, sortKey, sortFlip) : rows;
  // A pillow backed by a card always has See all — it opens the card (Ryan, Oct 14 2026: WO26154's NVE had none).
  const canExpand = source ? ((n || 0) > 3 || !!(p.opensCard && sj && !p.missing)) : !p.missing;
  // One card gets the full card, front and centre. Two or more get the list, because
  // a stack of full cards is unreadable.
  // One card with nothing in it yet (an NVE with no meters) is still that card on the job page.
  const single = !!(p.opensCard && sj && (n === 1 || (n === 0 && mine.length === 1)));

  // A pillow backed by a board card opens that card. One without a card — the punch
  // list, the POs — has nowhere else to go, so it expands in place instead.
  // Opening a row opens its own card — on drilling that's the card the row IS, so the
  // right one of several opens rather than always the first.
  const openItem = (which, item) => {
    // A workflow row IS a card — open it. A change order, submittal or ticket is one
    // item in a list, so open the list centred with that item already open, rather
    // than expanding the pillow and leaving them to find it again.
    if (p.opensCard && (which || sj)) return onOpenCard({ kind: p.opensCard, sj: which || sj, item });
    if (item && item.id && onOpenItem) return onOpenItem(p.key, item.id);
    return onExpand();
  };

  // A workflow pillow is one big button: tap anywhere on its face and the card comes up
  // front and centre (Ryan, Oct 2 2026) — before, only the title text did that, and a tap
  // on the preview or the empty space did nothing. Its own controls (add, remove, sort, a
  // row) still do their own thing.
  const facePick = p.opensCard && sj && !p.missing;
  const onFace = facePick ? e => { if (e.target.closest('button, select, input, textarea, a, label')) return; onExpand(); } : undefined;
  return (
    <div onClick={onFace} title={facePick ? 'Open the card' : undefined} style={{ ...card, display: 'flex', flexDirection: 'column', minWidth: 0,
      // A number is a window to scroll inside; no number lets it run, with a floor so a
      // pillow with nothing in it yet still looks like the others beside it, and a ceiling
      // of about a screen so one busy workflow can't push the row off the bottom.
      height: height || 'auto', minHeight: height ? undefined : 300, maxHeight: height ? undefined : 900,
      borderColor: expanded ? '#e87722' : '#e5e7eb', cursor: facePick ? 'pointer' : undefined,
      opacity: p.missing ? 0.6 : 1 }}>
      <div style={{ padding: '11px 13px 9px', borderBottom: rows && rows.length ? '1px solid #f1f1f1' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <button onClick={onExpand} disabled={p.missing}
            title={expanded ? 'Collapse' : 'Open full width'}
            style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, font: 'inherit',
              textAlign: 'left', cursor: p.missing ? 'default' : 'pointer',
              fontSize: 14, fontWeight: 700, color: p.missing ? '#9ca3af' : '#111',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</button>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
            padding: '3px 7px', borderRadius: 4, background: zs.bg, color: zs.fg, whiteSpace: 'nowrap' }}>
            {ZONE_LABEL[p.zone]}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            {p.missing ? 'not on this job' : (source ? (n == null ? 'loading…' : (n === 0 && sj && Number(sj.nve_pole_hold) ? '🪝 Pole hold' : countLabel(n, source.noun))) : '')}
          </span>
          {/* Not on this job yet — add it right here instead of hunting for "+ Add a workflow"
              (Pat, Oct 6 2026, WO26141's Submittals). */}
          {p.missing && p.sub && isAdminZone && (
            <button onClick={async () => {
              try { await apiFetch(`/projects/${projectId}/sub-jobs`, { method: 'POST', body: JSON.stringify({ type: p.sub, name: p.label }) }); onChanged && (await onChanged()); showToast && showToast(`${p.label} added to this job.`); }
              catch (e) { showToast && showToast(e?.error || 'Could not add it.'); }
            }} style={{ padding: '3px 10px', borderRadius: 999, border: '1px solid #fdba74', background: '#fff7ed', color: '#c2410c', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Add to this job
            </button>
          )}
          {source?.sorts && (n || 0) > 1 && (
            <select value={sortKey || ''} title="Sort this list"
              onChange={e => { const v = e.target.value; if (v === sortKey) setSortFlip(x => !x); else { setSortKey(v || null); setSortFlip(false); } }}
              style={{ ...selS, fontSize: 11, padding: '2px 4px' }}>
              <option value="">Sort…</option>
              {source.sorts.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
          )}
          {source?.sorts && sortKey && (
            <button onClick={() => setSortFlip(x => !x)} title="Reverse the order"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#c2410c' }}>
              {sortFlip ? '↑' : '↓'}
            </button>
          )}
          {/* A real button, not a whisper of a link (Pat, Oct 6 2026: "make the see all bigger"). */}
          {canExpand && (
            <button onClick={onExpand}
              style={{ marginLeft: 'auto', padding: '5px 13px', borderRadius: 999, border: '1px solid #fdba74', background: expanded ? '#fff' : '#fff7ed',
                cursor: 'pointer', fontSize: 13, fontWeight: 800, color: '#c2410c', whiteSpace: 'nowrap' }}>
              {expanded ? 'Collapse ▲' : 'See all ›'}
            </button>
          )}
        </div>
      </div>

      {/* The crews' Fieldwire pins for this workflow — opening the card shows them on
          the plan sheet. Quiet on jobs that aren't in Fieldwire or cards with no category. */}
      {!p.missing && !single && fw.tasks > 0 && (
        <button onClick={() => (p.opensCard && sj ? onOpenCard({ kind: p.opensCard, sj }) : onExpand())}
          title="Open the card to see these pins on the plan sheet"
          style={{ margin: '8px 12px 0', padding: '6px 10px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff',
            color: '#1e40af', fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
          📍 {fw.tasks} Fieldwire pin{fw.tasks === 1 ? '' : 's'} · {fw.done} done
        </button>
      )}

      {/* A single card is shown as the card, not as a list of one — as a picture of it:
          tapping it opens the real card front and centre, where the editing happens. */}
      {!p.missing && single && (
        <div style={{ padding: '10px 12px 2px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ pointerEvents: 'none' }}>
            <WorkflowCardInline kind={p.opensCard} sj={sj} project={project} pmName={pmName} onChanged={onChanged} />
          </div>
        </div>
      )}

      {!p.missing && !single && rows && rows.length > 0 && (
        <div style={{
          display: 'block',
          padding: '10px 12px 2px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {shown.map((it, i) => {
            const data = source.row ? source.row(it) : it;
            // On drilling every row IS its own card, so the row decides which one opens.
            // On USA, NVE and Loops the rows are what's INSIDE one card — tickets, meters,
            // locations — so they all open that card. Handing the ticket over as if it
            // were the card opened it against an id with nothing behind it, which is why
            // the ticket PDF and the rest of the card came up empty.
            const open = () => openItem(source.perSubJob ? mine[i] : sj, it);
            return source.columns
              ? <PoRow key={it.id || i} data={data} onOpen={open} />
              : <ItemRow key={it.id || i} data={data} onOpen={open} />;
          })}
        </div>
      )}

      {/* Pillows with no item list of their own still show their panel — capped to the
          same height as an item list, so one tall panel doesn't break the row. */}
      {!p.missing && !source && (
        <div style={{ padding: '0 13px 12px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <PillowBody pillow={p} projectId={projectId} project={project} bid={bid} items={items}
            sj={sj} embedded={embedded} isAdminZone={isAdminZone} role={role} showToast={showToast} navigate={navigate} isExec={isExec} onJobChanged={onJobChanged}
            onOpenItem={onOpenItem} />
        </div>
      )}

      {/* Add one — a ticket, a meter, a hole's card, a work order. This is where the
          data gets entered now, so it shouldn't need opening a card first. */}
      {isAdminZone && source?.add && (sj || !source.bySubJob) && (
        <button onClick={async () => {
          // Drilling's add has no endpoint of its own: it adds another CARD to the job.
          if (!source.add.post) { onAddCard(p.sub, source.add.asks); return; }
          const v = (window.prompt(source.add.asks) || '').trim();
          if (!v) return;
          try {
            const msg = await addPillowItem(source, { projectId, subJobId: sj?.id, value: v });
            setReloadKey(k => k + 1);
            if (onChanged) onChanged();
            showToast(msg);
          } catch (e) { showToast(e?.error || e?.message || 'Could not add that.'); }
        }}
          style={{ margin: '0 13px 10px', padding: '7px 10px', borderRadius: 8, border: '1px dashed #fed7aa',
            background: '#fff7ed', color: '#c2410c', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          + Add {source.add.label}
        </button>
      )}

      {/* Anything with nothing in it can come off the page. A pillow that carries a
          list is empty when the list is; one with no list of its own — a panel, a
          stub for something not moved across yet — can always go, because taking it
          off hides it rather than deleting anything. */}
      {isAdminZone && onRemove && (source ? n === 0 : true) && (
        <button onClick={() => onRemove(p, sj)}
          style={{ margin: '0 13px 10px', padding: '7px 10px', borderRadius: 8, border: '1px solid #fecaca',
            background: '#fff', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 'auto' }}>
          {source ? 'Remove from this job' : 'Take off this page'}
        </button>
      )}

      {canArrange && (
        <div style={{ display: 'flex', gap: 6, padding: '9px 13px', borderTop: '1px solid #f3f4f6', marginTop: (source ? n === 0 : true) ? 0 : 'auto' }}>
          <select value={p.tile} onChange={e => onMove(p.key, { tile: e.target.value })} style={{ ...selS, flex: 1, minWidth: 0 }} title="Which tile this pillow lives in">
            {TILES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
          <select value={p.zone} onChange={e => onMove(p.key, { zone: e.target.value })} style={{ ...selS, flex: 1, minWidth: 0 }} title="Who can see this pillow">
            <option value="1">Zone 1 · Office</option>
            <option value="2">Zone 2 · Field</option>
            <option value="both">Both</option>
          </select>
        </div>
      )}
    </div>
  );
}

// NVE is the odd one: its card is a single METER, which is why the board shows one
// card per address rather than one per job. So the row that was clicked has to travel
// with the sub-job. Everywhere else the card covers the whole workflow and the row is
// just something inside it.
function cardFor(kind, sj, project, item) {
  return workflowCardFromSubJob(sj, project, kind === 'nve' && item ? { meter_id: item.id } : {});
}

// The workflow's own card rendered inside a pillow, for the one-card case.
function WorkflowCardInline({ kind, sj, project, pmName, onChanged, item }) {
  const c = cardFor(kind, sj, project, item);
  if (kind === 'nve') return <NveCardModal card={c} pmName={pmName} onChanged={onChanged} inline />;
  if (kind === 'usa') return <UsaCardModal card={c} pmName={pmName} inline />;
  if (kind === 'loops') return <LoopsCardModal card={c} pmName={pmName} onChanged={onChanged} inline />;
  if (kind === 'note') return <NoteCardModal card={c} pmName={pmName} onChanged={onChanged} inline />;
  return <DrillCardModal card={c} pmName={pmName} onChanged={onChanged} inline />;
}

// The workflow's own card, opened centred over the page.
function WorkflowCardModal({ kind, sj, project, pmName, onChanged, onClose, item }) {
  const c = cardFor(kind, sj, project, item);
  if (kind === 'nve') return <NveCardModal card={c} pmName={pmName} onChanged={onChanged} onClose={onClose} />;
  if (kind === 'usa') return <UsaCardModal card={c} pmName={pmName} onClose={onClose} />;
  if (kind === 'loops') return <LoopsCardModal card={c} pmName={pmName} onChanged={onChanged} onClose={onClose} />;
  if (kind === 'note') return <NoteCardModal card={c} pmName={pmName} onChanged={onChanged} onClose={onClose} />;
  return <DrillCardModal card={c} pmName={pmName} onChanged={onChanged} onClose={onClose} />;
}


function Stub({ text }) {
  return <div style={{ fontSize: 13, color: '#9ca3af', padding: '4px 0' }}>{text}</div>;
}

